/**
 * Recipient Access Manager
 * Universal manager for voice-based recipient selection in forms
 * Handles events, polls, messages - any form with public/send_to_all checkbox and recipients selection
 */

export default class RecipientAccessManager {
    constructor(options = {}) {
        const translations = {
            ...getRecipientAccessTranslations(),
            ...(options.translations || {})
        };

        // Allow customization of field selectors
        this.publicCheckboxSelector = options.publicCheckboxSelector || '#id_public, #id_send_to_all';
        this.restrictedAccessSelector = options.restrictedAccessSelector || '.recipients-choice, .restricted-access';
        this.recipientsSelectSelector = options.recipientsSelectSelector || '#id_restricted_to, #id_recipients';
        this.voiceBulkSelectSelector = options.voiceBulkSelectSelector || '#voice-bulk-select';
        this.voiceBulkSectionSelector = options.voiceBulkSectionSelector || '.voice-bulk-selection';
        
        // Options for event-specific features
        this.enableHelpTexts = options.enableHelpTexts !== undefined ? options.enableHelpTexts : true;
        this.enableConductorHelp = options.enableConductorHelp !== undefined ? options.enableConductorHelp : true;
        this.voicePlaceholder = options.voicePlaceholder || translations.voicePlaceholder;
        this.publicHelpText = options.publicHelpText || translations.publicHelpText;
        this.conductorHelpText = options.conductorHelpText || translations.conductorHelpText;
        
        this.publicCheckbox = document.querySelector(this.publicCheckboxSelector);
        this.restrictedAccessGroup = document.querySelector(this.restrictedAccessSelector);
        this.recipientsSelect = document.querySelector(this.recipientsSelectSelector);
        this.voiceBulkSelect = document.querySelector(this.voiceBulkSelectSelector);
        this.voiceBulkSection = document.querySelector(this.voiceBulkSectionSelector);
        
        if (this.publicCheckbox && this.restrictedAccessGroup) {
            this.init();
        }
    }

    init() {
        // Set initial state
        this.updateRestrictedAccessVisibility();
        
        // Listen for public checkbox changes
        this.publicCheckbox.addEventListener('change', () => {
            this.updateRestrictedAccessVisibility();
        });
        
        // Initialize voice bulk selection functionality
        this.initVoiceBulkSelection();
        
        // Add help texts if enabled
        if (this.enableHelpTexts) {
            this.addHelpText();
        }
        if (this.enableConductorHelp) {
            this.addConductorHelpText();
        }
    }

    updateRestrictedAccessVisibility() {
        if (this.publicCheckbox.checked) {
            // If public/send_to_all is checked, hide restricted access and voice selection
            this.restrictedAccessGroup.style.display = 'none';
            if (this.voiceBulkSection) {
                this.voiceBulkSection.style.display = 'none';
            }
            // Don't clear selected recipients - user might have clicked by accident
        } else {
            // If not public, show restricted access options and voice selection
            this.restrictedAccessGroup.style.display = 'block';
            if (this.voiceBulkSection) {
                this.voiceBulkSection.style.display = 'block';
            }
        }
        
        // Update conductor help text visibility if enabled
        if (this.enableConductorHelp) {
            this.updateConductorHelpVisibility();
        }
    }

    addHelpText() {
        const publicLabel = this.publicCheckbox.closest('.form-check').querySelector('label');
        if (publicLabel && !publicLabel.querySelector('.help-text')) {
            const helpText = document.createElement('small');
            helpText.className = 'form-text text-muted help-text';
            helpText.textContent = this.publicHelpText;
            publicLabel.appendChild(document.createElement('br'));
            publicLabel.appendChild(helpText);
        }
    }

    addConductorHelpText() {
        const conductorSelect = document.getElementById('id_conductor');
        if (conductorSelect) {
            const conductorGroup = conductorSelect.closest('.form-group');
            if (conductorGroup && !conductorGroup.querySelector('.conductor-help-text')) {
                const helpText = document.createElement('small');
                helpText.className = 'form-text text-muted conductor-help-text';
                helpText.style.display = 'none'; // Initially hidden
                helpText.textContent = this.conductorHelpText;
                conductorGroup.appendChild(helpText);
                
                // Show/hide based on public checkbox state
                this.updateConductorHelpVisibility();
            }
        }
    }

    updateConductorHelpVisibility() {
        const helpText = document.querySelector('.conductor-help-text');
        if (helpText) {
            helpText.style.display = this.publicCheckbox.checked ? 'none' : 'block';
        }
    }

    initVoiceBulkSelection() {
        if (!this.voiceBulkSelect) {
            return;
        }
        
        // Load member voices map
        this.loadMemberVoicesMapFromDataAttribute();

        // Initialize Select2 and event handlers when ready
        $(document).ready(() => {
            // Check if Select2 is already initialized by main.js
            if (!$(this.voiceBulkSelect).hasClass('select2-hidden-accessible')) {
                $(this.voiceBulkSelect).select2({
                    placeholder: this.voicePlaceholder,
                    allowClear: true,
                    width: '100%'
                });
            }

            $(this.voiceBulkSelect).on('change', () => {
                this.processVoiceSelectionChange();
            });
            
            // Initialize voice selection based on currently selected recipients
            this.waitForSelect2AndInitialize();
        });
    }

    loadMemberVoicesMapFromDataAttribute() {
        const dataElement = document.getElementById('voice-mapping-data');
        const rawData = dataElement?.getAttribute('data-member-voices-map');
        
        try {
            this.memberVoicesMap = rawData ? JSON.parse(rawData) : {};
        } catch (e) {
            this.memberVoicesMap = {};
        }
    }

    processVoiceSelectionChange() {
        if (!this.memberVoicesMap || !this.recipientsSelect) return;

        const selectedVoices = $(this.voiceBulkSelect).val() || [];
        const $recipientSelect = $(this.recipientsSelect);
        
        // Initialize previous selection if not set
        this.previousVoiceSelection = this.previousVoiceSelection || [];

        // Detect changes
        const previousSet = new Set(this.previousVoiceSelection);
        const addedVoices = selectedVoices.filter(voiceId => !previousSet.has(voiceId));
        const removedVoices = this.previousVoiceSelection.filter(voiceId => !selectedVoices.includes(voiceId));

        let currentSelection = $recipientSelect.val() || [];

        // Add members from newly selected voices
        addedVoices.forEach(voiceId => {
            const voiceMembers = this.memberVoicesMap[voiceId] || [];
            voiceMembers.forEach(memberId => {
                const memberIdStr = memberId.toString();
                if (!currentSelection.includes(memberIdStr)) {
                    currentSelection.push(memberIdStr);
                }
            });
        });
        
        // Remove members from deselected voices
        removedVoices.forEach(voiceId => {
            const voiceMembers = this.memberVoicesMap[voiceId] || [];
            voiceMembers.forEach(memberId => {
                const memberIdStr = memberId.toString();
                
                // Check if member is still covered by other selected voices
                const stillCovered = selectedVoices.some(stillSelectedVoiceId => {
                    const stillSelectedMembers = this.memberVoicesMap[stillSelectedVoiceId] || [];
                    return stillSelectedMembers.includes(memberId);
                });
                
                // Remove only if not covered by other voices
                if (!stillCovered) {
                    const index = currentSelection.indexOf(memberIdStr);
                    if (index > -1) {
                        currentSelection.splice(index, 1);
                    }
                }
            });
        });

        this.previousVoiceSelection = [...selectedVoices];
        $recipientSelect.val(currentSelection).trigger('change');
    }

    /**
     * Initialize voice selection based on currently selected recipients
     * Called when form loads with pre-selected recipients (edit mode, validation errors)
     */
    initializeVoiceSelectionFromRecipients() {
        if (!this.memberVoicesMap || !this.recipientsSelect) {
            return;
        }

        const currentRecipients = $(this.recipientsSelect).val() || [];
        if (currentRecipients.length === 0) {
            return;
        }

        const voicesToSelect = this.detectFullySelectedVoices(currentRecipients);
        
        if (voicesToSelect.length > 0) {
            // Temporarily disable event handler to avoid loops
            $(this.voiceBulkSelect).off('change');
            
            // Reinitialize Select2 to ensure proper display
            const $select = $(this.voiceBulkSelect);
            $select.select2('destroy').select2({
                placeholder: this.voicePlaceholder,
                allowClear: true,
                width: '100%'
            });
            $select.val(voicesToSelect).trigger('change.select2');
            
            // Re-enable event handler
            $select.on('change', () => {
                this.processVoiceSelectionChange();
            });
            
            this.previousVoiceSelection = [...voicesToSelect];
        }
    }

    /**
     * Detect which voices are fully selected (all members of that voice are selected)
     * @param {Array} selectedMemberIds - Array of selected member IDs (as strings)
     * @returns {Array} - Array of voice IDs that are fully selected
     */
    detectFullySelectedVoices(selectedMemberIds) {
        const selectedMembersSet = new Set(selectedMemberIds.map(id => parseInt(id)));
        const fullySelectedVoices = [];

        // Check each voice in the mapping
        Object.keys(this.memberVoicesMap).forEach(voiceId => {
            const voiceMembers = this.memberVoicesMap[voiceId] || [];
            
            // Check if ALL members of this voice are selected
            const allMembersSelected = voiceMembers.length > 0 && 
                voiceMembers.every(memberId => selectedMembersSet.has(parseInt(memberId)));
            
            if (allMembersSelected) {
                fullySelectedVoices.push(voiceId);
            }
        });

        return fullySelectedVoices;
    }

    /**
     * Wait for both Select2 instances to be fully ready and then initialize
     */
    waitForSelect2AndInitialize() {
        // Wait a short moment for all Select2 instances to be ready
        setTimeout(() => {
            this.initializeVoiceSelectionFromRecipients();
        }, 500);
    }
}

// Auto-initialize with context-specific options when DOM is ready
$(document).ready(() => {
    if (!window.recipientAccessManagerInstance) {
        // Detect form type and use appropriate configuration
        const isEventForm = document.getElementById('id_public') !== null;
        const isPollForm = document.querySelector('form#create-poll-form') !== null;
        const isMessageForm = document.querySelector('form#add-message-form') !== null;
        
        let options = {};
        
        if (isEventForm) {
            // Event forms have conductor help and specific help text
            options = {
                enableHelpTexts: true,
                enableConductorHelp: true
            };
        } else if (isPollForm || isMessageForm) {
            // Polls and messages don't need conductor help
            options = {
                enableHelpTexts: false,
                enableConductorHelp: false
            };
        }
        
        window.recipientAccessManagerInstance = new RecipientAccessManager(options);
    }
});

function getRecipientAccessTranslations() {
    const dataElement = document.getElementById('voice-mapping-data');

    return {
        voicePlaceholder: dataElement?.dataset.voicePlaceholder || 'Select voices...',
        publicHelpText: dataElement?.dataset.publicHelpText || 'A public event is visible to all ensemble members',
        conductorHelpText: dataElement?.dataset.conductorHelpText || 'The selected conductor will automatically receive access to the event'
    };
}
